import * as _4 from 'lodash4';
import { CommunicatorService } from 'ui-ng-component-communicator';
import { DropCourseSectionsCommand } from '../commands/drop_course_sections_command';
import { SchedulerActiveTermQuery } from '../../../scheduler_active_term/queries/scheduler_active_term_query/scheduler_active_term_query';
import { SchedulerNotificationServant } from '../servants/scheduler_notification_servant';
import { SchedulerModel } from '../models/scheduler_model';
import { IApplicationConfiguration } from '../../../main';
import { ISection } from '../../../interfaces/scheduler_interfaces';
import { Strategy } from 'utility-ts-strategy';
import { ScheduledSectionTileServant } from '../servants/scheduled_section_tile_servant';
import { SectionSelectionsQuery } from '../../../components/section_selections/queries/section_selections_query';
import { WaitlistSectionCommand } from '../../unscheduled_sections/commands/waitlist_section_command';
import { PreventTransientAccessQuery } from '../../../prevent_transient_access/queries/prevent_transient_access_query';
import { UpdateSeatAvailabilityCommand } from '../commands/update_seat_availability_command';
import { MultiSectionsDropWithdrawRequestStateCommand } from '../../../request_state/commands/multi_sections_drop_withdraw_request_state_command';
import { MultiSectionsWaitlistRequestStateCommand } from '../../../request_state/commands/multi_sections_waitlist_request_state_command';

export type TMultiSelectMode = 'drop' | 'withdraw' | 'waitlist';

export class SelectedSectionActionsFacade {
  constructor(
    private ngSchedulerModel: SchedulerModel,
    private appConfigModel: IApplicationConfiguration,
    private ngSectionSelectionsQuery: SectionSelectionsQuery,
    private ngComponentCommunicatorService: CommunicatorService,
    private dropCourseSectionsCommand: DropCourseSectionsCommand,
    private ngSchedulerActiveTermQuery: SchedulerActiveTermQuery,
    private ngSchedulerNotificationServant: SchedulerNotificationServant,
    private ngScheduledSectionTileServant: ScheduledSectionTileServant,
    private waitlistSectionCommand: WaitlistSectionCommand,
    private ngPreventTransientAccessQuery: PreventTransientAccessQuery,
    private updateSeatAvailabilityCommand: UpdateSeatAvailabilityCommand,
    private multiSectionsDropWithdrawRequestStateCommand: MultiSectionsDropWithdrawRequestStateCommand,
    private multiSectionsWaitlistRequestStateCommand: MultiSectionsWaitlistRequestStateCommand
  ) {}

  isMultiDropFeatureEnabled() {
    return this.appConfigModel.isFeatureEnabled('reg-allow_multi_course_drop');
  }

  isMultiWaitlistFeatureEnabled() {
    return this.appConfigModel.isFeatureEnabled('reg-allow_multi_course_waitlist');
  }

  shouldShowMultiWaitlistFeature() {
    return this.isMultiWaitlistFeatureEnabled() && this.ngScheduledSectionTileServant.isWaitlistEnabled();
  }

  getWithdrawText() {
    const defaultWithdrawText = `
      <p>
        Dropping a course after the drop/add period has ended is considered a Withdrawal. You may withdraw
        from a course after the add/drop period has ended with no grade penalty, however,
        you may not be eligible for a tuition refund and may still have to pay any outstanding balances owed.
        You will receive a "W" grade for the course on your transcript when you withdraw.
        If you wish to withdraw from a course, you must do so by the withdraw deadline.
      </p>

      <p>
        Withdrawals are counted as an attempt for a course, please refer to your school’s Drop Policy
        regarding maximum attempts. Students who remain enrolled in courses after the withdrawal
        deadline has ended will be assigned a final grade in the course.
      </p>
    `;
    const serverText = _4.get(this.ngSchedulerModel.read(), 'customtext.WITHDRAW_CONFIRMATION.text', '');

    return _4.isEmpty(serverText) ? defaultWithdrawText : serverText;
  }

  private readonly __actionStrategy = new Strategy({
    error: (mode: TMultiSelectMode) => this.showNoSelectionError(mode),
    drop: () => this.ngComponentCommunicatorService.publish('OPEN_MULTI_DROP_MODAL'),
    withdraw: () => this.ngComponentCommunicatorService.publish('OPEN_MULTI_WITHDRAW_MODAL'),
    waitlist: () => this.ngComponentCommunicatorService.publish('OPEN_MULTI_WAITLIST_MODAL')
  });

  getSelectionByMode(mode: TMultiSelectMode) {
    const selectionStrategy = new Strategy({
      drop: () => this.ngSectionSelectionsQuery.result.dropSelections,
      withdraw: () => this.ngSectionSelectionsQuery.result.withdrawSelections,
      waitlist: () => this.ngSectionSelectionsQuery.result.waitlistSelections,
      fallback: () => [] as ISection[]
    });
    const invokeSelectionStrategy = selectionStrategy.get(mode);
    return invokeSelectionStrategy();
  }

  getSelectionCountByMode(mode: TMultiSelectMode) {
    const selection = this.getSelectionByMode(mode);
    return selection.length;
  }

  getSectionsInfoByMode(mode: TMultiSelectMode) {
    return this.getSelectionByMode(mode);
  }

  openMultiSectionsActionModal(mode: TMultiSelectMode) {
    const selectedSections = this.getSelectionByMode(mode);
    const noSelectionsForMode = _4.isEmpty(selectedSections);
    if (noSelectionsForMode) {
      const invokeActionStrategy = this.__actionStrategy.get('error');
      invokeActionStrategy(mode);
      return;
    }

    const invokeActionStrategy = this.__actionStrategy.get(mode);
    invokeActionStrategy(mode);
  }

  showNoSelectionError(mode: TMultiSelectMode) {
    const modeCapitalized = _4.capitalize(mode);
    this.ngSchedulerNotificationServant.showErrorNotification(
      `No Sections to ${modeCapitalized}`,
      `Please select a section to ${mode}.`
    );
  }

  invokeMultiSectionsAction(mode: TMultiSelectMode) {
    const multiSectionsActionStrategy = new Strategy({
      drop: () => this.multiSectionsDropOrWithdrawAction('drop'),
      withdraw: () => this.multiSectionsDropOrWithdrawAction('withdraw'),
      waitlist: () => this.multiSectionsWaitlistAction()
    });

    const invokeActionStrategy = multiSectionsActionStrategy.get(mode);
    invokeActionStrategy();
  }

  multiSectionsDropOrWithdrawAction(mode: TMultiSelectMode) {
    const selectedSectionIds = this.getSelectionByMode(mode).map((section) => section.id);
    const termId = _4.get(this.ngSchedulerActiveTermQuery.result, 'id', -1);
    this.multiSectionsDropWithdrawRequestStateCommand(true);
    this.dropCourseSectionsCommand(termId, [], true, -1, {}, selectedSectionIds);
  }

  multiSectionsWaitlistAction() {
    const selectedSectionIds = this.getSelectionByMode('waitlist').map((section) => section.id);
    const sectionPins = {};
    const regAttributesForSections = {};
    const studentCredentials = this.ngPreventTransientAccessQuery.result.output;
    this.multiSectionsWaitlistRequestStateCommand(true);
    this.waitlistSectionCommand(selectedSectionIds, sectionPins, regAttributesForSections, studentCredentials).then(
      () => this.updateSeatAvailabilityCommand(selectedSectionIds)
    );
  }
}

angular.module('capability-ng-scheduler').service('ngSelectedSectionActionsFacade', SelectedSectionActionsFacade);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/facades/selected_section_actions_facade.ts